<template>
  <div :class="['c-file-upload', typeof variant !== 'undefined' ? `c-file-upload--${variant}` : null]">

    <p class="c-file-upload__title" v-if="title">
      {{title}}
      <base-tooltip v-if="explanation" :content="explanation">
        <span class="info-extra" slot="reference">
          <font-awesome-icon :icon="['fal', 'info-circle']" />
        </span>
      </base-tooltip>
    </p>
    <div class="c-file-upload__activator-wrapper">
      <slot name="activator" v-bind="{ isLoading }">
        <div class="relative mb-2">
          <base-loading :is-active="isLoading" is-overlay />

          <div v-if="!previewImageUrl" class="c-file-upload__activator-area" @click="launchFilePicker()">
            <font-awesome-icon class="c-file-upload__activator-icon" :icon="['fal', 'image']" size="lg" />
            <span class="c-file-upload__activator-label">{{$t('sentence.clickHereToUploadImage')}}</span>
          </div>

          <img v-if="previewImageUrl && preview" :src="previewImageUrl" @click="launchFilePicker()" @error="replaceByDefault" class="c-file-upload__preview" alt="logo" />
        </div>

        <div class="flex items-center">
          <base-button variant="secondary" is-pill :title="uploadTitle" :disabled="isLoading" @click="launchFilePicker()" />
          <base-button variant="link" class="ml-auto c-file-upload__image-remove" :disabled="!value || isLoading" :title="$t('label.remove')" :icon-prepend="['fal', 'trash-alt']" @click="onRemoveClick"/>
        </div>
      </slot>
    </div>

    <input class="c-file-upload__file-button" type="file" ref="file" name="fileUpload" @change="onFileChange($event.target.files)" :text="$t('label.uploadImage')"/>

    <div class="c-file-upload__note">{{ $t('label.maxFileSize{size}', { size: formattedFileSize }) }}</div>
  </div>
</template>

<script>
import configuration from '@/classes/Configuration'

import imageService from 'Services/ImageService'
import ImageUpload from '@/classes/uploads/ImageUpload.js'

import { context } from '@/mixins/helpers'

import { readAndCompressImage } from 'browser-image-resizer'

const resizeConfiguration = {
  quality: 0.6,
  maxWidth: 800,
  maxHeight: 600,
  autoRotate: true,
  debug: false
}

const fileSizeLimit = 5242880 // 5 MB

export default {
  name: 'BaseFileUpload',

  mixins: [context],

  props: {
    value: {
      type: String,
      required: false
    },
    label: {
      type: String,
      default: 'Image'
    },
    variant: {
      type: String,
      required: false
    },
    preview: {
      type: Boolean,
      default: true
    },
    title: {
      type: String
    },
    explanation: {
      type: String
    },
    resize: {
      type: Boolean,
      default: false
    },
    resizeConfiguration: {
      type: Object
    }
  },

  data () {
    return {
      isLoading: false,

      previewImageUrl: null,
      uploadOptions: new ImageUpload(fileSizeLimit, 'image/jpeg, image/png', this.$i18n.t('sentence.acceptErrorMessageGeneric'))
    }
  },

  mounted () {
    this.$watch(() => this.value, value => {
      this.previewImageUrl = value ? `${configuration.get('portalApi')}/api/portal/images/${this.value}` : null
    }, { immediate: true })
  },

  computed: {
    /**
     * Default on resizeConfiguration
     * Values should validate in resize plugin
     */
    resizeConfigurationValidated () {
      return { ...resizeConfiguration, ...this.resizeConfiguration }
    },

    /**
     * Title if image is already uploaded
     */
    uploadTitle () {
      return this.previewImageUrl ? this.$i18n.t('label.uploadNew{label}', { label: this.label.toLowerCase() }) : this.$i18n.t('label.upload{label}', { label: this.label.toLowerCase() })
    },

    formattedFileSize () {
      return this.$formatBytes(fileSizeLimit, 0)
    }
  },

  methods: {
    launchFilePicker () {
      this.$refs.file.click()
    },

    replaceByDefault (event) {
      event.target.src = require('@/assets/images/no-image.svg')
      this.$emit('input', null)
    },

    /**
     * Save image when uploaded
     *
     * @param result - object { data; base64 result, mimeType }
     */
    saveImage (result) {
      this.isLoading = true

      imageService.save(this.context, result).then((result) => {
        // After upload, try to delete the old image
        if (this.value) {
          imageService.delete(this.context, this.value)
        }

        // Emit the image reference to the parent component after upload
        this.$emit('input', result.imageReference)
      }).finally(() => {
        this.isLoading = false
      })
    },

    onRemoveClick () {
      this.$store.dispatch('showModal', {
        component: {
          is: 'MessageModal',
          data: {
            message: this.$i18n.t('sentence.{label}DeleteConfirmation', { label: this.$i18n.t('label.image') })
          }
        },
        options: {
          title: this.$i18n.t('label.delete{label}', { label: this.$i18n.t('label.image') }),
          size: 'md'
        },
        events: {
          ok: () => this.removeImage()
        },
        actions: [{
          onClick: ({ cancel }) => cancel(),
          options: {
            title: this.$i18n.t('label.cancel'),
            variant: 'link',
            class: 'mr-3 btn-link--primary'
          }
        }, {
          onClick: ({ ok }) => ok(),
          options: {
            title: this.$i18n.t('label.delete'),
            variant: 'danger',
            isPill: true
          }
        }]
      })
    },

    /**
     * Remove uploaded image
    */
    removeImage () {
      this.isLoading = true

      imageService.delete(this.context, this.value).then(() => {
        // Clear input
        this.$refs.file.value = null

          // Emit the image reference to set null

          this.$emit('input', null)

          this.$nextTick(() => {
            this.$emit('remove')
          })
      }).finally(() => {
        this.isLoading = false
      })
    },

    /**
     * Handle file change
     *
     * @param images file object
     */
    onFileChange (images) {
      const [image] = images

      if (image && this.validateImage(image)) {
        this.resize ? this.resizeImage(image) : this.convertToBase64Image(image)
      }
    },

    /**
     * Validate the image
     *
     * @param image Image Blob
     */
    validateImage (image) {
      const { maximumSize, maximumSizeErrorMessage, accept, acceptErrorMessage } = this.uploadOptions

      let size = image.size / maximumSize

      if (!accept.includes(image.type)) {
        // check whether the upload is an image
        this.$store.dispatch('showMessage', { title: acceptErrorMessage, autoHideDelay: 5000, icon: ['fas', 'exclamation-triangle'], options: { variant: 'danger' } })

        return false
      } else if (size > 1) {
        // check whether the size is greater than the size limit
        this.$store.dispatch('showMessage', { title: maximumSizeErrorMessage, autoHideDelay: 5000, icon: ['fas', 'exclamation-triangle'], options: { variant: 'danger' } })

        return false
      }

      return true
    },

    /**
     * Resize image
     *
     * @param image Image Blob
     */
    resizeImage (image) {
      readAndCompressImage(image, { ...this.resizeConfigurationValidated, mimeType: image.type }).then(result => {
        this.convertToBase64Image(result)
      }).catch(error => console.error(error))
    },

    /**
     * Convert to base 64 image
     *
     * @param image Image Blob
     */
    convertToBase64Image (image) {
      let reader = new FileReader()
      reader.readAsDataURL(image)

      // Convert and save image
      reader.onload = () => {
        let result = reader.result.split(',')
        this.saveImage({ 'data': result[1], 'mimeType': result[0].match(/[^:\s*]\w+\/[\w-+\d.]+(?=[;| ])/)[0] })
      }

      reader.onerror = () => {
        this.$store.dispatch('showMessage', { title: this.$i18n.t('sentence.fileUploadException'), options: { variant: 'danger' } })
      }
    }
  }
}
</script>
